import { useEffect, useRef, useState } from 'react';
import { api } from '../../api/client';
import { Modal, Field, Empty, label } from '../ui.jsx';
import { useConfirm, useToast } from '../feedback.jsx';
import { t } from '../../i18n/index.jsx';
import { IconPlus } from '../icons.jsx';
import FileViewer from '../FileViewer.jsx';

const API_BASE = import.meta.env.VITE_API_BASE || '/api';
export const CONVERSATION_KINDS = ['call', 'meeting', 'email', 'whatsapp', 'note'];

function nowLocal() {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
}

export function blankConversation(clientId, projectId) {
  return { client_id: clientId || '', project_id: projectId || '', kind: 'note', happened_at: nowLocal(), summary: '' };
}

/**
 * Create/edit a conversation (call, meeting, email, WhatsApp note, or a
 * free-form note) with a client, plus attachments — recordings, PDFs,
 * screenshots, .eml exports. Audio attachments get a "Transcribe" button
 * that runs them through the AI assistant's speech-to-text pipeline.
 */
export default function ConversationForm({ initial, projects = [], onClose, onSaved }) {
  const confirm = useConfirm();
  const toast = useToast();
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [viewFile, setViewFile] = useState(null);
  const [transcribing, setTranscribing] = useState(null);
  const fileInput = useRef(null);
  const isNew = !initial.id;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  async function load() {
    if (!isNew) {
      const r = await api.get(`/conversations/${initial.id}`);
      setAttachments(r.attachments || []);
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [initial.id, isNew]);

  async function save() {
    setBusy(true); setErrors({});
    try {
      if (isNew) {
        const res = await api.post('/conversations', form);
        toast(t('Conversation logged'));
        onSaved(res.id);
      } else {
        await api.put(`/conversations/${initial.id}`, form);
        toast(t('Conversation saved'));
        onSaved(initial.id);
      }
    } catch (err) { setErrors(err.fields || { _: err.message }); }
    finally { setBusy(false); }
  }

  async function remove() {
    if (!(await confirm({ title: t('Delete conversation record'), message: t('Delete this conversation and its attachments?'), confirmLabel: t('Delete'), danger: true }))) return;
    await api.del(`/conversations/${initial.id}`);
    toast(t('Conversation deleted'));
    onSaved();
  }

  async function uploadFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    await api.upload(`/conversations/${initial.id}/attachments`, fd);
    await load();
    if (fileInput.current) fileInput.current.value = '';
  }

  async function transcribe(attId) {
    setTranscribing(attId);
    try {
      await api.post(`/conversation-attachments/${attId}/transcribe`);
      await load();
      toast(t('Transcription ready'));
    } catch (e) { toast(e.message, { tone: 'error' }); }
    finally { setTranscribing(null); }
  }

  const isAudio = (mime) => (mime || '').startsWith('audio/');

  return (
    <Modal title={isNew ? t('Log a conversation') : t('Edit conversation')} onClose={onClose}
      dirty={JSON.stringify(form) !== JSON.stringify(initial)}
      footer={
        <div className="modal-actions">
          {!isNew && <button className="btn danger ghost" onClick={remove}>{t('Delete')}</button>}
          <div className="spacer" />
          <button className="btn ghost" onClick={onClose}>{t('Cancel')}</button>
          <button className="btn primary" onClick={save} disabled={busy}>{busy ? t('Saving…') : t('Save')}</button>
        </div>
      }>
      {errors._ && <div className="alert error">{errors._}</div>}
      <div className="grid-2">
        <Field label={t('Type')}>
          <select value={form.kind} onChange={set('kind')}>
            {CONVERSATION_KINDS.map((k) => <option key={k} value={k}>{label(k)}</option>)}
          </select>
        </Field>
        <Field label={t('When')} error={errors.happened_at}>
          <input type="datetime-local" value={form.happened_at} onChange={set('happened_at')} />
        </Field>
      </div>
      <Field label={t('Project (optional)')}>
        <select value={form.project_id || ''} onChange={set('project_id')}>
          <option value="">—</option>
          {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </Field>
      <Field label={t('Summary')}><textarea rows={4} value={form.summary || ''} onChange={set('summary')} placeholder={t('What was discussed / agreed…')} /></Field>

      {!isNew && (
        <div className="files-block">
          <div className="panel-head">
            <h4>{t('Attachments')}</h4>
            <input ref={fileInput} type="file" hidden onChange={uploadFile} />
            <button className="btn ghost sm" type="button" onClick={() => fileInput.current?.click()}>
              <IconPlus width={13} height={13} /> {t('Add attachment')}
            </button>
          </div>
          {attachments.length === 0 ? <Empty>{t('No attachments.')}</Empty> : (
            <ul className="line-list">
              {attachments.map((a) => (
                <li key={a.id}>
                  <div className="ll-main">
                    <span className="ll-title">{a.original_name}</span>
                    {a.transcript && <span className="muted sm">{t('Transcribed')} · {a.transcript.slice(0, 80)}{a.transcript.length > 80 ? '…' : ''}</span>}
                  </div>
                  <div className="ll-meta">
                    <button type="button" className="btn ghost sm" onClick={() => setViewFile({ path: `/conversation-attachments/${a.id}/download`, name: a.original_name })}>{t('View')}</button>
                    <a className="btn ghost sm" href={`${API_BASE}/conversation-attachments/${a.id}/download`}>{t('Download')}</a>
                    {isAudio(a.mime_type) && !a.transcript && (
                      <button type="button" className="btn ghost sm" disabled={transcribing === a.id} onClick={() => transcribe(a.id)}>
                        {transcribing === a.id ? t('Transcribing…') : t('Transcribe')}
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      {viewFile && <FileViewer path={viewFile.path} name={viewFile.name} onClose={() => setViewFile(null)} />}
    </Modal>
  );
}
